import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchEmails, ingestEmail, fetchGmailStatus, gmailConnect, gmailDisconnect,
  gmailSync, submitEmailFeedback, uploadEmail, fetchInboundAddress,
  regenerateInboundAddress, fetchChildren,
} from "@/lib/api";
import { GMAIL, PASTE } from "@/constants/testIds";
import { formatDate } from "@/lib/format";
import {
  Mail, Inbox, ChevronRight, Plus, Sparkles, Zap, AlertCircle, X, Check,
  RefreshCw, Unplug, Paperclip, Upload, Forward, ChevronDown, Copy, RotateCcw,
  Pencil, ThumbsUp, ThumbsDown, UserCog, Tag,
} from "lucide-react";
import { toast } from "sonner";
import PageErrorBoundary from "@/components/PageErrorBoundary";

const CATEGORY_OPTIONS = [
  "School", "Sports", "Camp", "Medical", "Tutoring",
  "Payment", "Activity", "Other",
];

function safeDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function GmailInboxInner() {
  const [emails, setEmails] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState("child");
  const [intakeOpen, setIntakeOpen] = useState(null); // null | "paste" | "upload" | "forward"
  const [intakeMenuOpen, setIntakeMenuOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [gmail, setGmail] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState(30);
  const [children, setChildren] = useState([]);
  const [params, setParams] = useSearchParams();

  const reload = async () => {
    try {
      const data = await fetchEmails();
      const safe = (Array.isArray(data) ? data : []).filter(
        (e) => e && e.id && e.subject !== undefined,
      );
      setEmails(safe);
      setActive((prev) => {
        if (!safe.length) return null;
        if (!prev) return safe[0];
        return safe.find((e) => e.id === prev.id) || safe[0];
      });
    } catch (err) {
      console.error("fetchEmails failed", err);
      setEmails([]);
      setActive(null);
    }
  };

  const loadGmail = () =>
    fetchGmailStatus()
      .then((g) => setGmail(g || { configured: false, connected: false }))
      .catch(() => setGmail({ configured: false, connected: false }));

  useEffect(() => {
    reload();
    loadGmail();
    fetchChildren().then(setChildren).catch(() => setChildren([]));
  }, []);

  useEffect(() => {
    if (params.get("connected") === "1") {
      toast.success("Gmail connected. Sync to pull recent emails.");
      params.delete("connected");
      setParams(params, { replace: true });
      loadGmail();
    }
    if (params.get("error")) {
      toast.error(`Gmail connect failed: ${params.get("error")}`);
      params.delete("error");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConnect = async () => {
    try {
      const { authorize_url } = await gmailConnect();
      window.location.href = authorize_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start Gmail connect.");
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect KidHQ from this Gmail account?")) return;
    await gmailDisconnect();
    toast("Gmail disconnected.");
    loadGmail();
  };

  const onSync = async (daysOverride) => {
    setSyncing(true);
    const days = daysOverride ?? syncDays;
    const maxByWindow = days >= 365 ? 20 : days >= 90 ? 12 : 8;
    try {
      const r = await gmailSync({ days, max_messages: maxByWindow, scope: "kids_only" });
      const parts = [
        `Pulled ${r.new} new (${r.classified_child_related} child-related)`,
        `${r.skipped_already_ingested} already in`,
      ];
      if (r.deferred > 0) {
        const reason = r.deferred_budget > 0
          ? "AI classifier over budget — top up via Profile → Universal Key"
          : "AI classifier hit a transient error — retry shortly";
        toast.warning(
          `${parts.join(". ")}. ${r.deferred} message(s) queued for classification.`,
          { description: reason, duration: 14000 },
        );
      } else {
        toast.success(parts.join(". ") + ".");
      }
      reload();
      loadGmail();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        toast.error("Sync took too long — the connection timed out.", {
          description: "The backend may have finished some messages anyway. Reload the page or click Sync again to see what landed.",
          duration: 14000,
        });
        reload();
        loadGmail();
      } else if (detail && typeof detail === "object" && detail.code) {
        toast.error(detail.message || "Gmail sync failed.", {
          description: detail.hint || "",
          duration: 14000,
        });
      } else {
        toast.error(
          (typeof detail === "string" ? detail : null)
          || `Sync failed (HTTP ${err.response?.status || "?"}). Check backend logs or try a smaller window.`,
        );
      }
    } finally {
      setSyncing(false);
    }
  };

  const quickFeedback = async (email, kind) => {
    const labels = {
      child_related: "Child-related",
      not_child: "Adult",
      wrong_child: "Wrong child",
      wrong_category: "Wrong category",
    };
    try {
      await submitEmailFeedback(email.id, labels[kind] || kind, { feedback_kind: kind });
      toast.success("Saved — KidHQ will remember this for next time.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save feedback.");
    }
  };

  const filtered = (Array.isArray(emails) ? emails : []).filter((e) => {
    if (!e) return false;
    if (filter === "all") return true;
    if (filter === "child") return Boolean(e.is_child_related);
    if (filter === "review") return Boolean(e.is_child_related) && (e.confidence ?? 1) < 0.7;
    return !e.is_child_related;
  });

  return (
    <div data-testid={GMAIL.page} className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-overline">Family inbox</p>
          <h1 className="font-display text-4xl text-[var(--fcc-ink)] mt-2">Where every kid email arrives</h1>
          <p className="text-[var(--fcc-ink-soft)] mt-2 text-sm max-w-2xl">
            KidHQ matches on your kids&apos; names, school keywords, trusted senders (TeamSnap, ParentSquare, ClassDojo, SchoolMessenger, Remind…), and any senders you&apos;ve approved. Banking, marketing, and personal noise stay out.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <AddEmailDropdown
            open={intakeMenuOpen}
            setOpen={setIntakeMenuOpen}
            onPick={(kind) => { setIntakeMenuOpen(false); setIntakeOpen(kind); }}
          />
          <div className="flex gap-1 bg-white border border-[var(--fcc-border)] rounded-xl p-1 text-sm">
            {[
              {k:"child",l:"Child-related"},
              {k:"review",l:"Needs Review"},
              {k:"other",l:"Adult / Filtered"},
              {k:"all",l:"All"},
            ].map(o => (
              <button
                key={o.k}
                data-testid={`inbox-filter-${o.k}`}
                onClick={() => setFilter(o.k)}
                className={`px-3 py-1.5 rounded-lg ${filter === o.k ? "bg-[var(--fcc-ink)] text-white" : "text-[var(--fcc-ink-soft)]"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <GmailConnectCard
        gmail={gmail}
        syncing={syncing}
        onConnect={onConnect}
        onDisconnect={onDisconnect}
        onSync={onSync}
        syncDays={syncDays}
        setSyncDays={setSyncDays}
      />

      <OtherIntakeMethodsCard onPick={setIntakeOpen} />

      <div className="grid lg:grid-cols-12 gap-6">
        <ul className="lg:col-span-5 space-y-2">
          {filtered.map(e => (
            <li key={e.id} data-testid={GMAIL.emailRow}>
              <div className={`fcc-card p-4 transition ${active?.id === e.id ? "ring-2 ring-[var(--fcc-green)]" : ""}`}>
                <button onClick={() => setActive(e)} className="w-full text-left flex gap-3">
                  <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${e.is_child_related ? "bg-[var(--fcc-emma)]" : "bg-[var(--fcc-cream-2)]"}`}>
                    <Mail className="w-4 h-4 text-[var(--fcc-green)]" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-semibold text-[var(--fcc-ink)] truncate text-sm">{e.from_name}</p>
                      <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] shrink-0">{formatDate(e.received_at)}</span>
                    </div>
                    <p className="text-sm text-[var(--fcc-ink-soft)] truncate mt-0.5">{e.subject}</p>
                    <p className="text-xs text-[var(--fcc-muted)] truncate mt-0.5">{e.snippet}</p>
                    <div className="flex gap-2 mt-2 items-center">
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--fcc-muted)]">{(e.source || "demo")}</span>
                      {e.has_attachments && <Paperclip className="w-3 h-3 text-[var(--fcc-muted)]" />}
                      {!e.is_child_related && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">· filtered out</span>}
                      {e.parent_corrected && <span className="text-[10px] uppercase tracking-widest text-[var(--fcc-green)]">· you trained</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[var(--fcc-muted)] shrink-0" />
                </button>
                {filter === "review" && (
                  <NeedsReviewActions
                    onQuick={(k) => quickFeedback(e, k)}
                    onEdit={() => setEditingEmail(e)}
                  />
                )}
              </div>
            </li>
          ))}
          {filtered.length === 0 && <li className="text-sm text-[var(--fcc-muted)] p-6">No emails in this view yet.</li>}
        </ul>

        <div className="lg:col-span-7">
          {active ? (
            <div className="fcc-card p-8">
              <p className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">From</p>
              <p className="font-semibold text-[var(--fcc-ink)]">{active.from_name || "Unknown sender"} <span className="text-[var(--fcc-muted)] font-normal">&lt;{active.from_email || ""}&gt;</span></p>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-4">{active.subject || "(no subject)"}</h2>
              <p className="text-xs text-[var(--fcc-muted)] mt-1">{safeDateTime(active.received_at)} · source: <span className="font-semibold">{active.source || "demo"}</span></p>
              <pre className="whitespace-pre-wrap text-sm text-[var(--fcc-ink-soft)] mt-6 font-body leading-relaxed">{active.body || ""}</pre>
              {!active.is_child_related && <div className="mt-6 rounded-2xl bg-[var(--fcc-cream-2)] p-4 text-sm text-[var(--fcc-ink-soft)]">Claude classified this as not child-related — it stays out of your Detected list.</div>}

              <div className="mt-6 pt-5 border-t border-[var(--fcc-border)]">
                <p className="label-overline mb-2">Help KidHQ learn</p>
                <p className="text-xs text-[var(--fcc-muted)] mb-3">Quick correct, or pick &quot;Edit details&quot; for child / category overrides.</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="email-feedback-child-related"
                    onClick={() => quickFeedback(active, "child_related")}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-green-soft)] inline-flex items-center gap-1.5"
                  >
                    <ThumbsUp className="w-3 h-3" /> Child-related
                  </button>
                  <button
                    data-testid="email-feedback-not-child"
                    onClick={() => quickFeedback(active, "not_child")}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] inline-flex items-center gap-1.5"
                  >
                    <ThumbsDown className="w-3 h-3" /> Not child-related
                  </button>
                  <button
                    data-testid="email-feedback-wrong-child"
                    onClick={() => setEditingEmail(active)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] inline-flex items-center gap-1.5"
                  >
                    <UserCog className="w-3 h-3" /> Wrong child
                  </button>
                  <button
                    data-testid="email-feedback-wrong-category"
                    onClick={() => setEditingEmail(active)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] inline-flex items-center gap-1.5"
                  >
                    <Tag className="w-3 h-3" /> Wrong category
                  </button>
                  <button
                    data-testid="email-feedback-edit-details"
                    onClick={() => setEditingEmail(active)}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[var(--fcc-ink)] text-white hover:opacity-90 inline-flex items-center gap-1.5"
                  >
                    <Pencil className="w-3 h-3" /> Edit details
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="fcc-card p-8 text-center text-[var(--fcc-muted)]"><Inbox className="w-6 h-6 mx-auto" /><p className="mt-2">Pick an email to preview.</p></div>
          )}
        </div>
      </div>

      {intakeOpen === "paste" && (
        <PasteEmailDialog onClose={() => setIntakeOpen(null)} onSuccess={() => { setIntakeOpen(null); reload(); }} />
      )}
      {intakeOpen === "upload" && (
        <UploadEmailDialog onClose={() => setIntakeOpen(null)} onSuccess={() => { setIntakeOpen(null); reload(); }} />
      )}
      {intakeOpen === "forward" && (
        <ForwardingDialog onClose={() => setIntakeOpen(null)} />
      )}
      {editingEmail && (
        <EditEmailDetailsModal
          email={editingEmail}
          childList={children}
          onClose={() => setEditingEmail(null)}
          onSaved={() => { setEditingEmail(null); reload(); }}
        />
      )}
    </div>
  );
}

export default function GmailInbox() {
  return (
    <PageErrorBoundary scope="inbox">
      <GmailInboxInner />
    </PageErrorBoundary>
  );
}

function AddEmailDropdown({ open, setOpen, onPick }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [setOpen]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="add-email-dropdown-btn"
        className="fcc-btn-primary text-sm py-2.5 px-4 inline-flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> Add Email <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-[var(--fcc-border)] shadow-xl z-30 p-2"
             data-testid="add-email-dropdown-menu">
          <button
            data-testid="add-email-paste"
            onClick={() => onPick("paste")}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--fcc-cream-2)] text-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4 text-[var(--fcc-green)]" /> Paste Email
          </button>
          <button
            data-testid="add-email-upload"
            onClick={() => onPick("upload")}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--fcc-cream-2)] text-sm flex items-center gap-2"
          >
            <Upload className="w-4 h-4 text-[var(--fcc-green)]" /> Upload Email
            <span className="ml-auto text-[10px] text-[var(--fcc-muted)]">.eml .txt</span>
          </button>
          <button
            data-testid="add-email-forward"
            onClick={() => onPick("forward")}
            className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--fcc-cream-2)] text-sm flex items-center gap-2"
          >
            <Forward className="w-4 h-4 text-[var(--fcc-green)]" /> Forwarding Setup
          </button>
        </div>
      )}
    </div>
  );
}

function OtherIntakeMethodsCard({ onPick }) {
  return (
    <div className="fcc-card p-5 grid sm:grid-cols-3 gap-3" data-testid="other-intake-methods">
      <p className="sm:col-span-3 label-overline">Other ways to add emails</p>
      <button
        onClick={() => onPick("paste")}
        data-testid="intake-card-paste"
        className="text-left rounded-xl border border-[var(--fcc-border)] p-4 hover:bg-[var(--fcc-cream-2)] transition"
      >
        <Plus className="w-4 h-4 text-[var(--fcc-green)]" />
        <p className="font-semibold text-sm text-[var(--fcc-ink)] mt-2">Paste an email</p>
        <p className="text-xs text-[var(--fcc-muted)] mt-1">One-off copy-paste from any client.</p>
      </button>
      <button
        onClick={() => onPick("upload")}
        data-testid="intake-card-upload"
        className="text-left rounded-xl border border-[var(--fcc-border)] p-4 hover:bg-[var(--fcc-cream-2)] transition"
      >
        <Upload className="w-4 h-4 text-[var(--fcc-green)]" />
        <p className="font-semibold text-sm text-[var(--fcc-ink)] mt-2">Upload saved emails</p>
        <p className="text-xs text-[var(--fcc-muted)] mt-1">Drop a .eml or .txt file from your Mail app.</p>
      </button>
      <button
        onClick={() => onPick("forward")}
        data-testid="intake-card-forward"
        className="text-left rounded-xl border border-[var(--fcc-border)] p-4 hover:bg-[var(--fcc-cream-2)] transition"
      >
        <Forward className="w-4 h-4 text-[var(--fcc-green)]" />
        <p className="font-semibold text-sm text-[var(--fcc-ink)] mt-2">Forward school emails</p>
        <p className="text-xs text-[var(--fcc-muted)] mt-1">Get a unique <span className="font-mono">@inbox.kidhq.net</span> address.</p>
      </button>
    </div>
  );
}

function NeedsReviewActions({ onQuick, onEdit }) {
  return (
    <div className="mt-3 pt-3 border-t border-[var(--fcc-border)] flex flex-wrap gap-1.5" data-testid="needs-review-actions">
      <button data-testid="quick-action-child-related" onClick={() => onQuick("child_related")} className="px-2 py-1 rounded-md text-[11px] font-semibold bg-[var(--fcc-green-soft)] text-[var(--fcc-green)] hover:opacity-80 inline-flex items-center gap-1">
        <ThumbsUp className="w-3 h-3" /> Child-related
      </button>
      <button data-testid="quick-action-not-child" onClick={() => onQuick("not_child")} className="px-2 py-1 rounded-md text-[11px] font-semibold bg-[var(--fcc-cream-2)] text-[var(--fcc-ink-soft)] hover:opacity-80 inline-flex items-center gap-1">
        <ThumbsDown className="w-3 h-3" /> Not child-related
      </button>
      <button data-testid="quick-action-wrong-child" onClick={onEdit} className="px-2 py-1 rounded-md text-[11px] font-semibold bg-white border border-[var(--fcc-border)] hover:bg-[var(--fcc-cream-2)] inline-flex items-center gap-1">
        <UserCog className="w-3 h-3" /> Wrong child
      </button>
      <button data-testid="quick-action-wrong-category" onClick={onEdit} className="px-2 py-1 rounded-md text-[11px] font-semibold bg-white border border-[var(--fcc-border)] hover:bg-[var(--fcc-cream-2)] inline-flex items-center gap-1">
        <Tag className="w-3 h-3" /> Wrong category
      </button>
      <button data-testid="quick-action-edit-details" onClick={onEdit} className="px-2 py-1 rounded-md text-[11px] font-semibold bg-[var(--fcc-ink)] text-white hover:opacity-90 inline-flex items-center gap-1">
        <Pencil className="w-3 h-3" /> Edit details
      </button>
    </div>
  );
}

function EditEmailDetailsModal({ email, childList, onClose, onSaved }) {
  const [category, setCategory] = useState("");
  const [childId, setChildId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!category && !childId && !notes) {
      toast.error("Pick a category, a child, or add notes before saving.");
      return;
    }
    setSaving(true);
    try {
      const kind = category ? "wrong_category" : childId ? "wrong_child" : "child_related";
      const label = category || "Child Related";
      await submitEmailFeedback(email.id, label, {
        feedback_kind: kind,
        category: category || null,
        child_id: childId || null,
        notes: notes || null,
      });
      toast.success("Saved — KidHQ will use this correction for similar emails.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save feedback.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--fcc-ink)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="edit-email-details-modal" className="bg-white rounded-3xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-7 sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="label-overline">Train KidHQ</p>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-2">Edit details</h2>
              <p className="text-xs text-[var(--fcc-muted)] mt-1 truncate">{email?.subject || "(no subject)"}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl"><X className="w-4 h-4" /></button>
          </div>
          <form onSubmit={save} className="mt-5 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Child</label>
              <select
                data-testid="edit-email-child-select"
                value={childId}
                onChange={(e) => setChildId(e.target.value)}
                className="fcc-input mt-1 w-full"
              >
                <option value="">— No change —</option>
                {childList.filter(c => !c.is_demo).map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.grade ? ` · ${c.grade}` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Category</label>
              <select
                data-testid="edit-email-category-select"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="fcc-input mt-1 w-full"
              >
                <option value="">— No change —</option>
                {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Notes (optional)</label>
              <textarea
                data-testid="edit-email-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                className="fcc-input mt-1 w-full min-h-[80px]"
                placeholder="Anything we should know about this sender or topic?"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-semibold border border-[var(--fcc-border)] bg-white">Cancel</button>
              <button type="submit" data-testid="edit-email-save-btn" disabled={saving} className="fcc-btn-primary text-sm py-2 px-4">
                {saving ? "Saving…" : "Save feedback"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function UploadEmailDialog({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setLoading(true);
    try {
      // Read file as base64
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const r = reader.result || "";
          const idx = String(r).indexOf(",");
          resolve(idx >= 0 ? String(r).slice(idx + 1) : String(r));
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const r = await uploadEmail(file.name, b64);
      if (r?.classification?.is_child_related) {
        toast.success(`Classified as ${r.classification.category} · ${r.classification.priority}`);
      } else {
        toast("Uploaded — Claude classified it as not child-related.");
      }
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload that file.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--fcc-ink)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="upload-email-modal" className="bg-white rounded-3xl w-full max-w-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-7 sm:p-8">
          <div className="flex items-start justify-between">
            <div>
              <p className="label-overline">Upload an email</p>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-2">Drop a saved email file</h2>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-md leading-relaxed">
                Supported now: <span className="font-mono">.eml</span> · <span className="font-mono">.txt</span>. PDF upload coming soon.
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl"><X className="w-4 h-4" /></button>
          </div>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <input
              data-testid="upload-email-file-input"
              type="file"
              accept=".eml,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="fcc-input w-full"
              required
            />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm font-semibold border border-[var(--fcc-border)] bg-white">Cancel</button>
              <button type="submit" disabled={!file || loading} data-testid="upload-email-submit" className="fcc-btn-primary text-sm py-2 px-4">
                {loading ? "Classifying…" : "Upload & classify →"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ForwardingDialog({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchInboundAddress()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const copy = async () => {
    if (!data?.address) return;
    try {
      await navigator.clipboard.writeText(data.address);
      toast.success("Address copied.");
    } catch {
      toast.error("Couldn't copy — long-press the address to copy manually.");
    }
  };

  const regen = async () => {
    if (!window.confirm("Generate a fresh address? The old one stops working.")) return;
    setRegenerating(true);
    try {
      const d = await regenerateInboundAddress();
      setData((prev) => ({ ...(prev || {}), ...d }));
      toast.success("New forwarding address generated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't regenerate.");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--fcc-ink)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="forwarding-modal" className="bg-white rounded-3xl w-full max-w-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-7 sm:p-8">
          <div className="flex items-start justify-between">
            <div>
              <p className="label-overline">Forward school emails</p>
              <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-2">Your KidHQ inbox address</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl"><X className="w-4 h-4" /></button>
          </div>
          {loading ? (
            <p className="text-sm text-[var(--fcc-muted)] mt-6">Generating…</p>
          ) : !data ? (
            <p className="text-sm text-[var(--fcc-muted)] mt-6">Could not load address — try again.</p>
          ) : (
            <>
              <div className="mt-5 fcc-card p-4 bg-[var(--fcc-cream-2)] border-2 border-dashed border-[var(--fcc-border)]">
                <p className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]">Forward to</p>
                <p data-testid="forwarding-address" className="font-mono text-lg text-[var(--fcc-ink)] mt-1 break-all">{data.address}</p>
              </div>
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <strong>Coming soon.</strong> {data.status_message}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                <button onClick={copy} data-testid="forwarding-copy-btn" className="fcc-btn-primary text-sm py-2 px-4 inline-flex items-center gap-2">
                  <Copy className="w-3 h-3" /> Copy address
                </button>
                <button onClick={regen} disabled={regenerating} data-testid="forwarding-regen-btn" className="px-3 py-2 rounded-lg text-sm font-semibold border border-[var(--fcc-border)] bg-white inline-flex items-center gap-2">
                  <RotateCcw className={`w-3 h-3 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Regenerating…" : "Regenerate address"}
                </button>
              </div>
              <ul className="mt-6 text-sm text-[var(--fcc-ink-soft)] space-y-1.5">
                <li>• Works with any school system that emails parents</li>
                <li>• No Gmail connection required</li>
                <li>• Easiest setup for non-technical parents</li>
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GmailConnectCard({ gmail, syncing, onConnect, onDisconnect, onSync, syncDays, setSyncDays }) {
  if (!gmail) return null;

  const RANGE_OPTIONS = [
    { value: 30, label: "Last 1 month" },
    { value: 60, label: "Last 2 months" },
    { value: 90, label: "Last 3 months" },
    { value: 180, label: "Last 6 months" },
    { value: 270, label: "Last 9 months" },
    { value: 365, label: "Last 12 months" },
    { value: 3650, label: "All time (filtered by trusted senders + kids' names)" },
  ];

  if (gmail.connected) {
    const isAllTime = (syncDays ?? 30) >= 3650;
    return (
      <div data-testid={GMAIL.connectedBanner} className="fcc-card p-6 space-y-4 border-l-4 border-l-[var(--fcc-green)]">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-xl bg-[var(--fcc-green-soft)] flex items-center justify-center"><Check className="w-5 h-5 text-[var(--fcc-green)]" /></span>
            <div>
              <p className="font-display text-lg text-[var(--fcc-ink)]">Gmail connected · <span className="font-mono text-[var(--fcc-green)]">{gmail.gmail_email || "(account hidden)"}</span></p>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">
                {gmail.last_synced_at
                  ? <>Last synced {safeDateTime(gmail.last_synced_at)}.</>
                  : <>Not synced yet — pick a window below.</>}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <label className="text-xs uppercase tracking-widest text-[var(--fcc-muted)]" htmlFor="sync-range">Range</label>
            <select
              id="sync-range"
              data-testid="gmail-sync-range"
              value={syncDays}
              onChange={(e) => setSyncDays(Number(e.target.value))}
              className="fcc-input text-sm py-2 px-3"
              disabled={syncing}
            >
              {RANGE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button data-testid={GMAIL.syncBtn} onClick={() => onSync(syncDays)} disabled={syncing} className="fcc-btn-primary text-sm py-2.5 px-4">
              <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
            </button>
            <button data-testid={GMAIL.disconnectBtn} onClick={onDisconnect} className="fcc-btn-outline text-sm py-2.5 px-4">
              <Unplug className="w-4 h-4" /> Disconnect
            </button>
          </div>
        </div>
        <p className="text-xs text-[var(--fcc-muted)]">
          {isAllTime
            ? "All time scans your Gmail history — matched against your kids' names, trusted senders (TeamSnap, ParentSquare, ClassDojo, SchoolMessenger, Remind…), and senders you've approved. Banking & marketing stay out."
            : "Sync matches on your kids' names, school keywords, trusted senders (TeamSnap, ParentSquare, ClassDojo…), and senders you've approved. Banking & marketing are filtered at Gmail level."}
        </p>
      </div>
    );
  }

  if (!gmail.configured) {
    return (
      <div className="fcc-card-accent p-6 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <span className="w-11 h-11 rounded-xl bg-white border border-[var(--fcc-border)] flex items-center justify-center"><Mail className="w-5 h-5 text-[var(--fcc-green)]" /></span>
          <div>
            <p className="font-display text-lg text-[var(--fcc-ink)]">Connect Gmail · <span className="text-[var(--fcc-orange)]">Almost ready</span></p>
            <p className="text-sm text-[var(--fcc-ink-soft)] max-w-2xl mt-1">{gmail.message}</p>
          </div>
        </div>
        <button disabled className="rounded-xl px-4 py-2.5 text-sm font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-muted)] cursor-not-allowed inline-flex items-center gap-2">
          <Zap className="w-4 h-4" /> Connect Gmail
        </button>
      </div>
    );
  }

  return (
    <div className="fcc-card-accent p-6 flex flex-wrap items-center gap-4 justify-between">
      <div className="flex items-center gap-4">
        <span className="w-11 h-11 rounded-xl bg-white border border-[var(--fcc-border)] flex items-center justify-center"><Mail className="w-5 h-5 text-[var(--fcc-green)]" /></span>
        <div>
          <p className="font-display text-lg text-[var(--fcc-ink)]">Connect your Gmail</p>
          <p className="text-sm text-[var(--fcc-ink-soft)] max-w-2xl mt-1">Read-only access. Adult emails are filtered out before they ever reach your dashboard. PDF attachments (report cards, progress reports) are saved for the upcoming KidHQ Growth Journey module.</p>
        </div>
      </div>
      <button data-testid={GMAIL.connectBtn} onClick={onConnect} className="fcc-btn-primary text-sm py-2.5 px-4">
        <Zap className="w-4 h-4" /> Connect Gmail
      </button>
    </div>
  );
}

const SAMPLE = {
  from_name: "Coach Daniels",
  from_email: "coach@bayareasoccer.org",
  subject: "Emma's U10 game Saturday — Field 6 change",
  body: "Hi parents,\n\nQuick reminder: Emma is on snack duty for Saturday's 10am match. Bring orange slices for 12 kids. The game has moved from Field 2 to Field 6 at Lakeview Sports Complex. Arrive 30 minutes early for warm-up.\n\nGo Eagles!\nCoach Daniels",
};

function PasteEmailDialog({ onClose, onSuccess }) {
  const [form, setForm] = useState({ from_name: "", from_email: "", subject: "", body: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const r = await ingestEmail({ ...form, source: "paste" });
      setResult(r);
      if (r.classification?.is_child_related) {
        toast.success(`Classified as ${r.classification.category} · ${r.classification.priority}`);
      } else {
        toast("Adult email — kept out of your Detected list.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not classify this email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[var(--fcc-ink)]/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-8 sm:p-10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="label-overline">Live Claude Sonnet 4.5</p>
              <h2 className="font-display text-3xl text-[var(--fcc-ink)] mt-2">Paste an email</h2>
              <p className="text-sm text-[var(--fcc-ink-soft)] mt-2 max-w-xl">Forward or paste any real email. Claude reads it, extracts deadlines / events / payments / forms, and (if child-related) adds it to your Detected list as a real task + calendar suggestion.</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[var(--fcc-cream-2)] rounded-xl"><X className="w-4 h-4 text-[var(--fcc-ink-soft)]" /></button>
          </div>

          <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-3">
            <input data-testid={PASTE.fromName} className="fcc-input" placeholder="Sender name (e.g. Coach Daniels)" value={form.from_name} onChange={e => setForm({...form, from_name: e.target.value})} required />
            <input data-testid={PASTE.fromEmail} type="email" className="fcc-input" placeholder="sender@example.com" value={form.from_email} onChange={e => setForm({...form, from_email: e.target.value})} required />
            <input data-testid={PASTE.subject} className="fcc-input sm:col-span-2" placeholder="Email subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} required />
            <textarea data-testid={PASTE.body} className="fcc-input sm:col-span-2 min-h-[180px] font-body" placeholder="Paste the full email body…" value={form.body} onChange={e => setForm({...form, body: e.target.value})} required />
            <div className="sm:col-span-2 flex items-center justify-between gap-3">
              <button type="button" onClick={() => setForm(SAMPLE)} className="text-sm text-[var(--fcc-green)] font-semibold inline-flex items-center gap-1"><Sparkles className="w-4 h-4" /> Use sample</button>
              <button data-testid={PASTE.submit} disabled={loading} type="submit" className="fcc-btn-primary text-sm">
                {loading ? "Classifying…" : "Classify with Claude →"}
              </button>
            </div>
          </form>

          {result && (
            <div data-testid={PASTE.result} className="mt-6">
              {result.classification?.is_child_related ? (
                <div className="rounded-2xl border border-[var(--fcc-green)] bg-[var(--fcc-green-soft)] p-5">
                  <p className="font-semibold text-[var(--fcc-green)] flex items-center gap-2"><Check className="w-4 h-4" /> Classified as {result.classification.category} · {result.classification.priority}</p>
                  <dl className="mt-3 grid sm:grid-cols-2 gap-3 text-sm text-[var(--fcc-ink-soft)]">
                    {result.classification.child_name && <Row k="Child" v={result.classification.child_name} />}
                    {result.classification.summary && <Row k="Summary" v={result.classification.summary} span />}
                    {result.classification.action_required && <Row k="Action required" v={result.classification.action_required} span />}
                    {result.classification.due_date && <Row k="Due" v={new Date(result.classification.due_date).toLocaleString()} />}
                    {result.classification.event_date && <Row k="Event" v={new Date(result.classification.event_date).toLocaleString()} />}
                    {result.classification.location && <Row k="Location" v={result.classification.location} />}
                    {result.classification.payment_amount && <Row k="Payment" v={`$${result.classification.payment_amount.toFixed(2)}`} />}
                    {result.classification.required_items?.length > 0 && <Row k="Bring" v={result.classification.required_items.join(", ")} span />}
                  </dl>
                  <div className="mt-4 flex flex-wrap gap-2 text-xs">
                    {result.task && <span className="fcc-pill bg-white text-[var(--fcc-green)]">Task created</span>}
                    {result.calendar_suggestion && <span className="fcc-pill bg-white text-[var(--fcc-green)]">Calendar suggestion added</span>}
                  </div>
                  <button onClick={onSuccess} className="fcc-btn-primary mt-5 text-sm py-2.5">Done — open inbox</button>
                </div>
              ) : (
                <div className="rounded-2xl border border-[var(--fcc-border)] bg-[var(--fcc-cream-2)] p-5 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-[var(--fcc-muted)] mt-0.5" />
                  <div>
                    <p className="font-semibold text-[var(--fcc-ink)]">Claude classified this as not child-related.</p>
                    <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">{result.classification?.matched_keywords?.join(", ") || "No child / school / activity signal detected."} It stays out of your Detected list.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, span }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <dt className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)]">{k}</dt>
      <dd className="mt-0.5 text-[var(--fcc-ink)]">{v}</dd>
    </div>
  );
}
